import { getFieldErrors } from "@onadssuper/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardBody, EmptyState, PageHeader, PageSpinner } from "../../../../components/ui";
import { CampaignForm, emptyCampaignForm } from "../../../../features/campaigns/campaign-form";
import { useCan, useCurrentWorkspace } from "../../../../features/workspace/workspace-context";
import { useTRPC } from "../../../../lib/trpc";

export const Route = createFileRoute("/w/$slug/campaigns/new")({ component: NewCampaign });

function NewCampaign() {
  const workspace = useCurrentWorkspace();
  const canCreate = useCan("campaign.create");
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Already loaded by the workspace layout; the creator owns the campaign by default.
  const me = useQuery(trpc.me.get.queryOptions());
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const back = () => void navigate({ to: "/w/$slug/campaigns", params: { slug: workspace.slug } });

  const create = useMutation(
    trpc.campaigns.create.mutationOptions({
      meta: { silent: true },
      onSuccess: async (campaign) => {
        toast.success(`Campaign "${campaign.name}" created`);
        await queryClient.invalidateQueries({ queryKey: trpc.campaigns.list.pathKey() });
        await navigate({
          to: "/w/$slug/campaigns/$campaignId",
          params: { slug: workspace.slug, campaignId: campaign.id },
        });
      },
      onError: (error) => {
        const fields = getFieldErrors(error);
        setServerErrors(fields);
        setFormError(Object.keys(fields).length ? undefined : error.message);
      },
    }),
  );

  if (!canCreate)
    return <EmptyState title="Campaigns are created by agency owners, admins and strategists" />;
  if (!me.data) return <PageSpinner />;

  return (
    <>
      <PageHeader
        title="New campaign"
        description="The record everything else hangs off: the brief, tasks, decisions and, later, assets and results."
      />
      <Card className="max-w-3xl">
        <CardBody>
          <CampaignForm
            initial={emptyCampaignForm(me.data.user.id)}
            submitLabel="Create campaign"
            submitting={create.isPending}
            serverErrors={serverErrors}
            formError={formError}
            onSubmit={(input) => create.mutate(input)}
            onCancel={back}
          />
        </CardBody>
      </Card>
    </>
  );
}
